import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaUsers,
  FaProjectDiagram,
  FaExclamationCircle,
  FaChartLine,
  FaHeadset,
  FaAddressBook,
  FaHome,
  FaLock,
  FaUserTie,
  FaWallet,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronDown,
  FaChevronUp,
  FaBullhorn,
  FaFolderOpen,
  FaCogs,
  FaCoins,
  FaTasks,
  FaBook,
  FaCalendarAlt,
  FaUser,
  FaKey,
  FaCog,
  FaSignOutAlt,
  FaGlobe,
} from "react-icons/fa";
import "../assets/css/Sidebar.css";
import { getCurrentUserRole, getAllowedSidebarPages } from "../utils/roleAccess";
import { getSidebarTheme, sidebarThemeClassName } from "../utils/sidebarTheme";
import { getAuthItem } from "../utils/authStorage";
import { resolveImageUrl, getApiUrl } from "../config/apiConfig";
import { clearAuthSessionStorage } from "../utils/clearSessionStorage";

const SIDEBAR_GROUPS_STORAGE_KEY = "cloudOfficeSidebarGroupsOpen_v3";

const PAGE_LABELS = {
  Dashboard: "Dashboard",
  Users: "Employees",
  Projects: "Projects",
  UserStories: "User Stories",
  Tasks: "Tasks",
  Issues: "Issue Tracker",
  DigixLeads: "Leads",
  WebsiteLeads: "Website Leads",
  DMProjects: "Clients",
  DigitalMarketing: "Calendar",
  Tenants: "Clients",
  Accounts: "Vault",
  HRManagement: "HR Management",
  FinanceManagement: "Finance",
  "Support Contacts": "Support",
};

const PAGE_ICONS = {
  Dashboard: FaHome,
  Users: FaUsers,
  Projects: FaProjectDiagram,
  UserStories: FaBook,
  Tasks: FaTasks,
  Issues: FaExclamationCircle,
  DigixLeads: FaChartLine,
  WebsiteLeads: FaGlobe,
  DMProjects: FaFolderOpen,
  DigitalMarketing: FaCalendarAlt,
  Tenants: FaAddressBook,
  Accounts: FaLock,
  HRManagement: FaUserTie,
  FinanceManagement: FaWallet,
  "Support Contacts": FaHeadset,
};

const SIDEBAR_STRUCTURE = [
  { type: "item", id: "Dashboard" },
  { type: "item", id: "Tenants" },
  {
    type: "group",
    id: "pms",
    label: "PMS",
    icon: FaCogs,
    items: ["Projects", "UserStories", "Tasks", "Issues"],
  },
  {
    type: "group",
    id: "dm",
    label: "DMS",
    icon: FaBullhorn,
    items: ["DMProjects", "DigitalMarketing", "DigixLeads", "WebsiteLeads"],
  },
  {
    type: "group",
    id: "hrms",
    label: "HRMS",
    icon: FaUserTie,
    items: ["Users", "HRManagement"],
  },
  {
    type: "group",
    id: "fms",
    label: "FMS",
    icon: FaCoins,
    items: ["FinanceManagement"],
  },
  { type: "divider", id: "after-fms" },
  { type: "item", id: "Accounts" },
  { type: "item", id: "Support Contacts" },
];

const DEFAULT_OPEN_GROUPS = {
  pms: true,
  dm: false,
  hrms: false,
  fms: false,
};

function readOpenGroups() {
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_OPEN_GROUPS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_OPEN_GROUPS, ...parsed };
  } catch {
    return { ...DEFAULT_OPEN_GROUPS };
  }
}

function findGroupIdForPage(pageId) {
  for (const entry of SIDEBAR_STRUCTURE) {
    if (entry.type === "group" && entry.items.includes(pageId)) return entry.id;
  }
  return null;
}

function readUser() {
  try {
    const userStr = getAuthItem("user");
    if (userStr) return JSON.parse(userStr);
  } catch {
    /* ignore */
  }
  return null;
}

function Sidebar({
  activePage,
  onNavigate,
  isOpen = false,
  collapsed = false,
  onToggleCollapse,
  onLogout,
  onMyDetails,
  onChangePassword,
  onPermissions,
  onSettings,
}) {
  const location = useLocation();
  const role = getCurrentUserRole();
  const allowedPages = getAllowedSidebarPages(role);
  const [theme, setTheme] = useState(() => getSidebarTheme("office"));
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  const [user, setUser] = useState(() => readUser());
  const [imageError, setImageError] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const sync = (e) => {
      if (e?.detail?.scope === "office" && e.detail.theme) {
        setTheme(e.detail.theme);
        return;
      }
      setTheme(getSidebarTheme("office"));
    };
    window.addEventListener("sidebarThemeChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("sidebarThemeChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    const load = () => setUser(readUser());
    load();
    const onStorage = (e) => {
      if (e.key === "user" || e.key === null) load();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("userDataUpdated", load);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("userDataUpdated", load);
    };
  }, []);

  useEffect(() => {
    setImageError(false);
  }, [user?.user_id, user?.image_url, user?.image_path]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const onDoc = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [profileMenuOpen]);

  const allowedSet = useMemo(() => {
    const s = new Set(allowedPages);
    if (s.has("Projects")) {
      s.add("UserStories");
      s.add("Tasks");
    }
    return s;
  }, [allowedPages]);

  const structure = useMemo(() => {
    const mapped = SIDEBAR_STRUCTURE.map((entry) => {
      if (entry.type === "divider") return entry;
      if (entry.type === "item") {
        return allowedSet.has(entry.id) ? entry : null;
      }
      const items = entry.items.filter((id) => allowedSet.has(id));
      if (items.length === 0) return null;
      return { ...entry, items };
    }).filter(Boolean);

    return mapped.filter((entry, index) => {
      if (entry.type !== "divider") return true;
      const hasBefore = mapped.slice(0, index).some((e) => e.type !== "divider");
      const hasAfter = mapped.slice(index + 1).some((e) => e.type !== "divider");
      return hasBefore && hasAfter;
    });
  }, [allowedSet]);

  useEffect(() => {
    const groupId = findGroupIdForPage(activePage);
    if (!groupId) return;
    setOpenGroups((prev) => {
      if (prev[groupId]) return prev;
      const next = { ...prev, [groupId]: true };
      try {
        localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [activePage]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      try {
        localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const isAdminUser = (() => {
    if (!user) return false;
    const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
    const roleName = (
      (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
      user.role_name ||
      user.role ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase();
    return roleName === "admin";
  })();

  const userName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || "User"
    : "User";
  const userEmail = user ? user.email || user.company_email || "" : "";
  const userInitial = user?.first_name ? user.first_name.charAt(0).toUpperCase() : "U";

  const userImageUrl = (() => {
    if (!user) return null;
    let imageUrl = null;
    if (user.image_url) {
      imageUrl = resolveImageUrl(user.image_url);
    } else if (user.image_path) {
      const API_HOST = getApiUrl();
      if (API_HOST) {
        imageUrl = `${API_HOST.replace(/\/$/, "")}/uploads/cloud-office/users/${user.image_path}`;
      }
    }
    if (!imageUrl) return null;
    const separator = imageUrl.includes("?") ? "&" : "?";
    return `${imageUrl}${separator}t=${user.updated_on || Date.now()}`;
  })();

  const handleSignOut = () => {
    clearAuthSessionStorage();
    sessionStorage.clear();
    if (onLogout) onLogout();
  };

  const runProfileAction = (fn) => {
    setProfileMenuOpen(false);
    if (typeof fn === "function") fn();
  };

  const renderNavItem = (itemId) => {
    const Icon = PAGE_ICONS[itemId];
    const label = PAGE_LABELS[itemId] || itemId;
    return (
      <div
        key={itemId}
        role="button"
        tabIndex={0}
        className={`nav-item ${activePage === itemId ? "active" : ""}`}
        onClick={() => onNavigate && onNavigate(itemId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onNavigate && onNavigate(itemId);
          }
        }}
        title={label}
      >
        {Icon && <Icon className="nav-item-icon" />}
        <span className="nav-item-label">{label}</span>
      </div>
    );
  };

  const profileMenuActive = ["/my-details", "/change-password", "/permissions", "/settings"].some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  return (
    <div
      className={`sidebar${isOpen ? " is-open" : ""}${collapsed ? " is-collapsed" : ""} ${sidebarThemeClassName(theme)}`}
    >
      <div className="sidebar-header">
        <div className="sidebar-logo" title="CLOUD OFFICE">
          {collapsed ? "CO" : "CLOUD OFFICE"}
        </div>
        {onToggleCollapse && (
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <FaAngleDoubleRight size={14} /> : <FaAngleDoubleLeft size={14} />}
          </button>
        )}
      </div>

      <nav className="sidebar-nav">
        {structure.map((entry) => {
          if (entry.type === "divider") {
            return <div key={entry.id} className="sidebar-nav-divider" role="separator" />;
          }
          if (entry.type === "item") {
            return renderNavItem(entry.id);
          }

          const GroupIcon = entry.icon;
          const isOpenGroup = collapsed ? true : !!openGroups[entry.id];
          const hasActiveChild = entry.items.includes(activePage);

          return (
            <div
              key={entry.id}
              className={`sidebar-group${isOpenGroup ? " is-open" : ""}${hasActiveChild ? " has-active" : ""}`}
            >
              <button
                type="button"
                className="sidebar-group-toggle"
                onClick={() => !collapsed && toggleGroup(entry.id)}
                aria-expanded={isOpenGroup}
                title={entry.label}
              >
                {GroupIcon && <GroupIcon className="nav-item-icon" />}
                <span className="nav-item-label sidebar-group-label">{entry.label}</span>
                {!collapsed && (
                  <FaChevronDown
                    className={`sidebar-group-chevron${isOpenGroup ? " is-open" : ""}`}
                    size={11}
                  />
                )}
              </button>
              {isOpenGroup && (
                <div className="sidebar-group-items" role="group" aria-label={entry.label}>
                  {entry.items.map((itemId) => renderNavItem(itemId))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer" ref={profileRef}>
        <div className="sidebar-nav-divider sidebar-footer-divider" role="separator" />

        {profileMenuOpen ? (
          <div className="sidebar-profile-menu" role="menu" aria-label="Account menu">
            <button
              type="button"
              className="sidebar-profile-menu-item"
              role="menuitem"
              onClick={() => runProfileAction(onMyDetails)}
            >
              <FaUser className="sidebar-profile-menu-icon" aria-hidden />
              <span>My Details</span>
            </button>
            <button
              type="button"
              className="sidebar-profile-menu-item"
              role="menuitem"
              onClick={() => runProfileAction(onChangePassword)}
            >
              <FaKey className="sidebar-profile-menu-icon" aria-hidden />
              <span>Change Password</span>
            </button>
            {isAdminUser ? (
              <button
                type="button"
                className="sidebar-profile-menu-item"
                role="menuitem"
                onClick={() => runProfileAction(onPermissions)}
              >
                <FaLock className="sidebar-profile-menu-icon" aria-hidden />
                <span>Permissions</span>
              </button>
            ) : null}
            <button
              type="button"
              className="sidebar-profile-menu-item"
              role="menuitem"
              onClick={() => runProfileAction(onSettings)}
            >
              <FaCog className="sidebar-profile-menu-icon" aria-hidden />
              <span>Settings</span>
            </button>
            <div className="sidebar-profile-menu-divider" role="separator" />
            <button
              type="button"
              className="sidebar-profile-menu-item sidebar-profile-menu-item--danger"
              role="menuitem"
              onClick={() => runProfileAction(handleSignOut)}
            >
              <FaSignOutAlt className="sidebar-profile-menu-icon" aria-hidden />
              <span>Sign out</span>
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className={`sidebar-account-trigger${profileMenuOpen || profileMenuActive ? " is-active" : ""}`}
          onClick={() => setProfileMenuOpen((v) => !v)}
          aria-expanded={profileMenuOpen}
          aria-haspopup="menu"
          title={userName}
        >
          <span className="sidebar-account-avatar" aria-hidden>
            {userImageUrl && !imageError ? (
              <img src={userImageUrl} alt="" onError={() => setImageError(true)} />
            ) : (
              userInitial
            )}
          </span>
          <span className="sidebar-account-details">
            <span className="sidebar-account-name">{userName}</span>
            <span className="sidebar-account-email">{userEmail || "—"}</span>
          </span>
          <FaChevronUp
            className={`sidebar-account-caret${profileMenuOpen ? " is-open" : ""}`}
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
